import { type FormEvent, useId } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { type FormikProps } from 'formik';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { useAnnouncementMediaUploads } from '@/app/pages/notifications/announcements/hooks/useAnnouncementMediaUploads';
import AnnouncementFormFields from '@/app/pages/notifications/announcements/components/AnnouncementFormFields';
import { type AnnouncementFormValues } from '@/app/pages/notifications/announcements/core/_models';
import '@/app/pages/notifications/announcements/modal/AnnouncementFormDialog.scss';

type AnnouncementFormDialogProps = {
  show: boolean;
  title: string;
  formik: FormikProps<AnnouncementFormValues>;
  isSaving: boolean;
  isPublishing: boolean;
  /** Origem ainda carregando ou com falha: salvar mandaria o formulário vazio ou sobrescreveria o que chegar. */
  isSaveBlocked: boolean;
  sourceId?: string;
  onClose: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
};

/** A moldura que criar e editar compartilham; cada modal decide de onde vêm os valores e para onde vão. */
function AnnouncementFormDialog({
  show,
  title,
  formik,
  isSaving,
  isPublishing,
  isSaveBlocked,
  sourceId,
  onClose,
  onSaveDraft,
  onPublish,
}: AnnouncementFormDialogProps) {
  const titleId = useId();
  const isSavingDraft = isSaving && !isPublishing;
  const publishLabel = formik.values.startsNow ? 'Publicar' : 'Agendar publicação';
  const changeBody = (body: string) => {
    void formik.setFieldValue('body', body, false);
  };
  const registerMedia = (attachment: AttachmentDto) => {
    formik.setFormikState((state) => {
      const attachments = state.values.attachments.filter((media) => media.id !== attachment.id);
      const values: AnnouncementFormValues = { ...state.values, attachments: [...attachments, attachment] };
      return { ...state, values };
    });
  };
  const mediaUploads = useAnnouncementMediaUploads({
    body: formik.values.body,
    onBodyChange: changeBody,
    onMediaUploaded: registerMedia,
    active: show && !isSaveBlocked,
    sourceId,
    initialValues: formik.initialValues,
  });
  // Os campos seguem editáveis enquanto a mídia envia; só salvar e publicar esperam o envio.
  const isFormLocked = isSaving || isSaveBlocked;
  const isActionDisabled = isFormLocked || mediaUploads.isBlocked;
  const draftButtonLabel = isSavingDraft ? 'Salvando...' : 'Salvar rascunho';
  const publishButtonLabel = isPublishing ? 'Publicando...' : publishLabel;
  const saveDraft = () => {
    if (isActionDisabled) return;
    onSaveDraft();
  };
  const publish = () => {
    if (isActionDisabled) return;
    onPublish();
  };
  const resetForm = () => {
    mediaUploads.reset();
    formik.resetForm();
  };
  // Enquanto salva, o diálogo não fecha: a resposta do pedido fecharia e limparia um formulário reaberto.
  const closeDialog = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveDraft();
  };

  return (
    <Modal
      centered
      scrollable
      aria-labelledby={titleId}
      contentClassName="h-100"
      dialogClassName="announcement-form-modal mx-auto"
      show={show}
      onExited={resetForm}
      onHide={closeDialog}
    >
      <form className="announcement-form d-flex flex-column h-100 overflow-hidden" onSubmit={handleSubmit}>
        <Modal.Header closeButton className="px-6 px-md-8 py-5" closeLabel="Fechar">
          <div className="d-flex align-items-center gap-4">
            <div>
              <Modal.Title as="h2" className="fs-3 fw-bold mb-1" id={titleId}>
                {title}
              </Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="announcement-form-body d-grid p-0">
          <AnnouncementFormFields
            disabled={isFormLocked}
            formik={formik}
            uploadEntries={mediaUploads.entries}
            onMediaSelected={mediaUploads.enqueue}
            onRemoveMediaUpload={mediaUploads.remove}
            onRetryMediaUpload={mediaUploads.retry}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button className="text-gray-600" disabled={isSaving} variant="secondary" onClick={closeDialog}>
            Cancelar
          </Button>
          <Button disabled={isActionDisabled} type="submit" variant="secondary">
            <i aria-hidden="true" className="bi bi-save me-2" />
            {draftButtonLabel}
          </Button>
          <Button disabled={isActionDisabled} type="button" variant="primary" onClick={publish}>
            <i aria-hidden="true" className="bi bi-send me-2" />
            {publishButtonLabel}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default AnnouncementFormDialog;
